/**
 * Dashboards a connector declares, as this app reads them, with no React in it.
 *
 * `GET /insights/dashboards` serves each as `{ dashboard, source, title,
 * description, charts: [{ title, spec, chart }] }`, where a chart is a report
 * spec: the same JSON `POST /insights/query` takes. The kernel checked every one
 * against its own schema when the connector was built, so what arrives is
 * askable. This turns each into what the rest of the app already knows how to
 * show: a dashboard of charts, each a form `state`, which is what lets it be
 * narrowed by shared filters and opened to its records like one a person made.
 *
 * ## Read-only, and built from nothing the person owns
 *
 * A declared dashboard is not copied into the person's own. It is rebuilt from
 * the kernel's answer each time, cannot be edited or deleted here, and holds no
 * figure: opening it asks the kernel each question as the person.
 *
 * ## Which are shown, and which charts
 *
 * A dashboard appears when the person can open at least one record of a type one
 * of its charts is about, which is what "after the connector's first sync" comes
 * to for them: before it, there is nothing to count. Inside it a chart that
 * comes back with nothing is left off and not drawn as an empty tile, so a
 * person who can open invoices and not payments sees the invoice charts and no
 * hole. Nothing says that anything was left off.
 */
import { NONE_LABEL } from "./spec.js";

const valueText = (op, value) => {
  if (op === "is_set" || op === "is_not_set" || value === undefined || value === null) return "";
  return Array.isArray(value) ? value.join(", ") : String(value);
};

/** A spec as the form holds it: text for values, empty strings for "none". */
export function stateFromSpec(spec, chart = "auto") {
  return {
    type: spec.type,
    measures: (spec.measures || []).map((m) => ({ fn: m.fn, field: m.field || "" })),
    dimensions: (spec.dimensions || []).map((d) => ({ field: d.field, bucket: d.bucket || "" })),
    filters: (spec.filters || []).map((f) => ({ field: f.field, op: f.op, value: valueText(f.op, f.value) })),
    chart: chart || "auto",
  };
}

/** A declared dashboard as the dashboards screen holds one: read-only, with no figures. */
export function fromDeclared(d) {
  return {
    id: `declared:${d.dashboard}`,
    name: d.title,
    readOnly: true,
    source: d.source,
    description: d.description || "",
    items: d.charts.map((c, i) => ({
      id: `${d.dashboard}:${i}`,
      title: c.title,
      state: stateFromSpec(c.spec, c.chart),
    })),
    filters: [],
    range: { from: "", to: "" },
  };
}

/**
 * The ones to offer, given how many records of each type the person can open
 * (`counts` is `{ [type]: number }`, from the overview's own counts). One that
 * is about nothing they can open is not offered, and is not mentioned.
 */
export function offered(declared, counts) {
  if (!counts) return [];
  return (declared || []).map(fromDeclared)
    .filter((d) => d.items.some((i) => Number(counts[i.state.type]) > 0))
    .sort((a, b) => a.source.localeCompare(b.source) || a.name.localeCompare(b.name));
}

/**
 * Whether an answer has nothing in it worth a tile: no rows, or a figure whose
 * every measure is zero or missing. A refusal or a failure is never this: it is
 * a fact about the platform and is shown, not dropped.
 */
export function nothingToShow(result) {
  const rows = result?.rows || [];
  if (!rows.length) return true;
  const cols = result.columns || [];
  if (cols.some((c) => c.role === "dimension")) return false;
  const measures = cols.filter((c) => c.role === "measure");
  return measures.length > 0 && measures.every((m) => {
    const v = rows[0][m.key];
    return v === null || v === undefined || v === NONE_LABEL || !Number(v);
  });
}
